/**
 * MediaQualityHint – shared UI for showing a calm quality summary
 * with optional expandable explanation.
 *
 * Reusable across all media uploader contexts (place, area, producer, activity).
 *
 * Usage:
 *   <MediaQualityHint coverAsset={activeCover} />
 */

import { useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { deriveMediaQualityState, type MediaQualityResult } from '@/lib/media/deriveMediaQualityState';
import type { MediaAsset } from '@/lib/media/mediaTypes';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface MediaQualityHintProps {
  coverAsset: MediaAsset | null;
  className?: string;
  /** Image intent for context-aware quality thresholds. Required — no silent hero default. */
  intent: import('@/lib/media/imageIntent').ImageIntent;
}

const stateStyles: Record<MediaQualityResult['state'], string> = {
  good: 'text-emerald-600',
  improvable: 'text-amber-600',
  missing: 'text-muted-foreground',
};

const stateIcons: Record<MediaQualityResult['state'], React.ReactNode> = {
  good: <Check className="h-3 w-3" />,
  improvable: <Info className="h-3 w-3" />,
  missing: null,
};

export function MediaQualityHint({ coverAsset, className, intent }: MediaQualityHintProps) {
  const quality = useMemo(() => deriveMediaQualityState(coverAsset, intent), [coverAsset, intent]);
  const [open, setOpen] = useState(false);

  // Don't render anything when no images exist at all
  if (quality.state === 'missing') return null;

  if (!quality.hasDetails) {
    // Simple non-clickable summary
    return (
      <div className={cn('flex items-center gap-1 text-xs', stateStyles[quality.state], className)}>
        {stateIcons[quality.state]}
        <span>{quality.label}</span>
      </div>
    );
  }

  // Clickable with expandable explanation
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1 text-xs transition-colors',
            'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded',
            stateStyles[quality.state],
          )}
        >
          {stateIcons[quality.state]}
          <span>{quality.label}</span>
          {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1.5 rounded-md border border-border bg-muted/40 px-3 py-2 space-y-1">
          {quality.details.map((line, i) => (
            <p key={i} className="text-xs text-muted-foreground leading-relaxed">
              {line}
            </p>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
